import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BadgeComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  FormControlDirective,
  FormSelectDirective,
  PageItemDirective,
  PageLinkDirective,
  PaginationComponent,
  RowComponent,
  SpinnerComponent,
  TableDirective
} from '@coreui/angular-pro';
import { TranslatePipe } from 'i18n';
import { ViacarteAdminService, ViacarteEntityWallet, ViacarteUserListItem, ViacarteWalletPocket } from '../../../services/viacarte-admin.service';

@Component({
  selector: 'app-admin-viacarte-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ContainerComponent,
    RowComponent,
    ColComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    ButtonDirective,
    TableDirective,
    BadgeComponent,
    SpinnerComponent,
    FormControlDirective,
    FormSelectDirective,
    PaginationComponent,
    PageItemDirective,
    PageLinkDirective,
    TranslatePipe
  ],
  templateUrl: './admin-viacarte-page.component.html',
  styleUrls: ['./admin-viacarte-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminViacartePageComponent {
  private service = inject(ViacarteAdminService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  users = signal<ViacarteUserListItem[]>([]);
  loading = signal(false);
  error = signal<string | null>(null);
  page = signal(1);
  perPage = 20;
  totalCount = signal(0);
  stats = signal<Record<string, number>>({});

  statusFilter = signal<string>('');
  searchFilter = signal<string>('');

  entityBalance = signal<ViacarteEntityWallet | null>(null);
  entityBalanceError = computed<string | null>(() => {
    const b = this.entityBalance();
    return b && b.error ? b.error : null;
  });
  // Anzeige-Reihenfolge: Pockets (je Währung) -> totalWalletBalance -> das
  // Balance-Objekt (availableBalanceAmount). Letzteres greift, wenn ViaCarte
  // weder Pockets noch totalWalletBalance liefert (Sandbox-Fall).
  entityPockets = computed<ViacarteWalletPocket[]>(() => {
    const b = this.entityBalance();
    if (!b || b.error) return [];
    if (b.pockets && b.pockets.length) return b.pockets;
    if (b.totalWalletBalance != null) {
      return [{ currencyName: b.balance?.balanceCurrency || '', balance: b.totalWalletBalance }];
    }
    if (b.balance && b.balance.availableBalanceAmount != null) {
      return [{ currencyName: b.balance.balanceCurrency || '', balance: b.balance.availableBalanceAmount }];
    }
    return [];
  });

  // Status-Werte für das Filter-Dropdown (gemäß ViaCarte-Taxonomie).
  readonly statuses = [
    'IN_PROGRESS', 'PENDING_REVIEW', 'PENDING_ADDITIONAL_DOCS', 'WITH_EXCEPTION_FILES',
    'ACCEPTED', 'COMPLETED', 'FAILED', 'DECLINED', 'REJECTED'
  ];

  totalPages = computed(() => Math.max(1, Math.ceil(this.totalCount() / this.perPage)));

  visiblePages = computed(() => {
    const current = this.page();
    const total = this.totalPages();
    const pages: number[] = [];
    const start = Math.max(1, current - 2);
    const end = Math.min(total, current + 2);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  });

  private searchPrimed = false;

  constructor() {
    this.load();

    // Debounced Suche – der erste (Initial-)Lauf wird übersprungen, da der
    // Konstruktor bereits geladen hat.
    effect((onCleanup) => {
      this.searchFilter(); // Abhängigkeit registrieren
      if (!this.searchPrimed) { this.searchPrimed = true; return; }
      const timeout = setTimeout(() => {
        this.page.set(1);
        this.load();
      }, 400);
      onCleanup(() => clearTimeout(timeout));
    });
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.service.list({
      page: this.page(),
      per_page: this.perPage,
      status: this.statusFilter() || undefined,
      search: this.searchFilter().trim() || undefined
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.loading.set(false);
          this.users.set(res.users ?? []);
          this.totalCount.set(res.total_count ?? 0);
          this.stats.set(res.stats ?? {});
          this.entityBalance.set(res.entity_balance ?? null);
        },
        error: () => {
          this.loading.set(false);
          this.error.set('Failed to load ViaCarte users');
        }
      });
  }

  onStatusFilterChange(): void {
    this.page.set(1);
    this.load();
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages()) return;
    this.page.set(p);
    this.load();
  }

  openDetail(user: ViacarteUserListItem): void {
    const lang = localStorage.getItem('language') || 'en';
    this.router.navigateByUrl(`/${lang}/admin/viacarte/${user.id}`);
  }

  statName(status: string): number {
    return this.stats()[status] ?? 0;
  }

  shortId(id: string | null): string {
    if (!id) return '—';
    return id.length > 12 ? `${id.slice(0, 8)}…` : id;
  }

  formatDate(d: string | null | undefined): string {
    if (!d) return '—';
    return new Date(d).toLocaleString();
  }

  formatAmount(amount: number | undefined | null, currency?: string): string {
    if (amount == null) return '—';
    return `${Number(amount).toFixed(2)} ${currency || ''}`.trim();
  }
}
